/**
 * Widget API layer — deliberately standalone rather than sharing
 * `@minirag/api`. That package is built around the dashboard's login session
 * (in-memory access token + httpOnly refresh cookie via `openapi-fetch`),
 * which is a different credential model entirely, and pulling it in would
 * also blow the widget's size budget for a client neither pillar of it
 * needs. The SSE parsing logic below is ported from
 * `dashboard/widget/minirag-widget.js` (and mirrors
 * `packages/api/src/stream.ts`) — behavior kept identical, only the
 * transport (site key -> scoped token, not a login session) changed.
 */

export interface WidgetSession {
  token: string;
  expiresAt: number;
  botProfileId: string;
  tenantId: string;
}

export class WidgetSessionError extends Error {}

export async function fetchWidgetSession(apiUrl: string, siteKey: string): Promise<WidgetSession> {
  let resp: Response;
  try {
    resp = await fetch(`${apiUrl}/v1/widget/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site_key: siteKey }),
    });
  } catch {
    throw new WidgetSessionError('Could not reach the chat service.');
  }

  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new WidgetSessionError(body.detail ?? `Chat session failed (${resp.status})`);
  }

  const data = (await resp.json()) as {
    token: string;
    expires_in: number;
    bot_profile_id: string;
    tenant_id: string;
  };
  return {
    token: data.token,
    expiresAt: Date.now() + data.expires_in * 1000,
    botProfileId: data.bot_profile_id,
    tenantId: data.tenant_id,
  };
}

export interface WidgetTheme {
  primaryColor: string | null;
  headerColor: string | null;
  launcherColor: string | null;
  sendButtonColor: string | null;
  userBubbleColor: string | null;
  userTextColor: string | null;
  botBubbleColor: string | null;
  botTextColor: string | null;
  windowBgColor: string | null;
  position: 'bottom-left' | 'bottom-right' | null;
  title: string | null;
  suggestedQuestions: string[];
  inputPlaceholder: string | null;
  launcherShadow: string | null;
  launcherMotion: string | null;
  launcherSize: string | null;
  launcherOffsetBottom: number | null;
  launcherOffsetSide: number | null;
  iconUrl: string | null;
}

/**
 * Cosmetic config set from the dashboard's Customize dialog. Fetched eagerly
 * on mount (unlike the session token, which waits for the first click) so
 * the closed bubble paints with the tenant's chosen color from the start —
 * see index.ts. Failures here are silent: a widget that can't reach this
 * endpoint should still render with sane defaults, not break.
 */
export async function fetchWidgetTheme(
  apiUrl: string,
  siteKey: string,
): Promise<WidgetTheme | null> {
  try {
    const resp = await fetch(`${apiUrl}/v1/widget/theme?site_key=${encodeURIComponent(siteKey)}`);
    if (!resp.ok) return null;

    const data = (await resp.json()) as Record<string, string | number | null>;
    const asNum = (v: unknown): number | null =>
      typeof v === 'number' && Number.isFinite(v) ? v : null;
    const asStr = (v: unknown): string | null => (typeof v === 'string' ? v : null);

    return {
      primaryColor: asStr(data.primary_color),
      headerColor: asStr(data.header_color),
      launcherColor: asStr(data.launcher_color),
      sendButtonColor: asStr(data.send_button_color),
      userBubbleColor: asStr(data.user_bubble_color),
      userTextColor: asStr(data.user_text_color),
      botBubbleColor: asStr(data.bot_bubble_color),
      botTextColor: asStr(data.bot_text_color),
      windowBgColor: asStr(data.window_bg_color),
      position:
        data.position === 'bottom-left' || data.position === 'bottom-right' ? data.position : null,
      title: asStr(data.title),
      suggestedQuestions: Array.isArray(data.suggested_questions)
        ? (data.suggested_questions as unknown[])
            .filter((q): q is string => typeof q === 'string' && q.trim().length > 0)
            .slice(0, 4)
        : [],
      inputPlaceholder: asStr(data.input_placeholder),
      launcherShadow: asStr(data.launcher_shadow),
      launcherMotion: asStr(data.launcher_motion),
      launcherSize: asStr(data.launcher_size),
      launcherOffsetBottom: asNum(data.launcher_offset_bottom),
      launcherOffsetSide: asNum(data.launcher_offset_side),
      // Relative to the API's own origin, not the host page's — must be
      // resolved against apiUrl here, not left for the <img> to resolve
      // against whatever page this widget is embedded on.
      iconUrl: typeof data.icon_url === 'string' ? new URL(data.icon_url, apiUrl).href : null,
    };
  } catch {
    return null;
  }
}

export interface StreamDone {
  chat_id: string;
  message_id: string;
  guardrail?: {
    action: 'redact' | 'block';
    rules: string[];
  };
}

/** No `sources` handler: the backend withholds retrieved passages from
 * widget sessions (`include_sources` in app/api/v1/chat.py) — a visitor gets
 * the answer only. */
export interface ChatStreamHandlers {
  onDelta?: (content: string) => void;
  onDone?: (data: StreamDone) => void;
  /** `retryAfterSeconds` is set when the failure was a 429 (rate limit). */
  onError?: (message: string, retryAfterSeconds?: number) => void;
}

export async function streamChat(
  apiUrl: string,
  token: string,
  botProfileId: string,
  message: string,
  chatId: string | undefined,
  { onDelta, onDone, onError }: ChatStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let resp: Response;
  try {
    resp = await fetch(`${apiUrl}/v1/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        bot_profile_id: botProfileId,
        message,
        stream: true,
        ...(chatId ? { chat_id: chatId } : {}),
      }),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError?.((err as Error).message || 'Network error');
    return;
  }

  if (!resp.ok || !resp.body) {
    const body = (await resp.json().catch(() => ({}))) as { detail?: string };
    const retryAfter = resp.status === 429 ? Number(resp.headers.get('Retry-After')) : NaN;
    onError?.(
      body.detail ?? `HTTP ${resp.status}`,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        if (!frame.trim()) continue;

        let eventType = 'message';
        let dataStr = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.slice(7);
          else if (line.startsWith('data: ')) dataStr += line.slice(6);
        }
        if (!dataStr) continue;

        let data: unknown;
        try {
          data = JSON.parse(dataStr);
        } catch {
          continue;
        }

        switch (eventType) {
          case 'delta':
            onDelta?.((data as { content: string }).content);
            break;
          case 'done':
            onDone?.(data as StreamDone);
            break;
          case 'error':
            onError?.((data as { detail?: string }).detail ?? 'Stream error');
            break;
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError?.((err as Error).message || 'Stream read error');
  } finally {
    reader.releaseLock();
  }
}

export async function submitFeedback(
  apiUrl: string,
  token: string,
  chatId: string,
  messageId: string,
  feedback: 'positive' | 'negative' | null,
): Promise<boolean> {
  try {
    const resp = await fetch(`${apiUrl}/v1/chat/${chatId}/messages/${messageId}/feedback`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ feedback }),
    });
    return resp.ok;
  } catch {
    return false;
  }
}
